import React from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withScope } from '@bingads-webui-react/scope-service';
import { CampaignType } from '@bingads-webui-campaign-react/shell-ui-common';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Glyphicon } from '@bingads-webui-react/primitive';
import { Pivot, PivotItem, PivotLinkSize, FocusZone, IconButton } from '@fluentui/react';
import { FallbackCallout } from '@bingads-webui-react/fallback-feature';
import { SecondaryNavigationExpandButton } from '@bingads-webui-campaign-react/secondary-navigation-expand-button';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { isNavigationRefreshOn } from '@bingads-webui-react/preference-utility';
import {
  siteMapPropType,
  withSiteMap,
  processor,
  navRefreshSiteMap
} from '@bingads-webui-campaign-react/with-site-map';
import { processorSet } from '@bingads-webui-react/with-site-map';
import { withComponentErrorLogging } from '@bingads-webui-campaign-react/global-error-options';

import { getTabBarStyles } from '../styles/tab-bar-styles';

const DROP_DOWN_PIVOT_ITEM = 'dropdown-pivot';

const navRefreshProcessorSet = [
  processor.processImportCenterMode,
  processor.processMixedModeAdGroup,
  processor.processGlobalFilter,
  processor.processCampaignType,
  processor.processCampaignId,
  processor.processSmartCampaign,
  processor.processSmartShoppingCampaign,
  processor.processHotelCampaign,
  processor.processPmaxCampaignPageMenu,
  processor.processPmaxRetailCampaignPageMenu,
  ...processorSet.navRefresh,
];

const ExpandButton = withComponentErrorLogging(withSiteMap(SecondaryNavigationExpandButton, navRefreshProcessorSet, navRefreshSiteMap), 'NavigationRefreshSecondaryNavigationExpandButton');

class TabBar extends React.PureComponent {
  static defaultProps = { fallBackTabItem: null };

  constructor(props) {
    super(props);
    const {
      classes,
      className,
      tabItems,
      responsiveSize,
    } = props;

    const subMenuClassNamePrefix = 'bingads-campaign-react-tab-bar';
    this.menuContainerPartClassName = classNames([
      classes.menuContainer,
      subMenuClassNamePrefix,
      className,
      'large-links',
      `maxTabSize${responsiveSize}`]);
    if (tabItems.length === 1) {
      this.menuContainerPartClassName = classNames([this.menuContainerPartClassName, this.props.classes.onlyOneTabItem]);
    }
    this.moreButtonRef = React.createRef();
  }

  onSelectPivotItem = (item) => {
    if (item.props.itemKey !== DROP_DOWN_PIVOT_ITEM) {
      this.props.onSelect(item.props.itemKey);
    } else {
      this.moreButtonRef.current.openMenu();
    }
  };

  genereateOverflowMenuItemProps = (leftOverItems) => {
    const { i18n } = this.props;
    const menuProps = {};
    menuProps.items = [];
    _.each(leftOverItems, (item, index) => {
      menuProps.items[index] = {};
      const menuItem = menuProps.items[index];
      menuItem.key = item.id;
      menuItem.text = i18n.getString(item.name);
      menuItem.onClick = () => this.props.onSelect(item.id);
    });
    return menuProps;
  };

  generateTabItemsFragment = (items) => {
    const tabItemsFragement = items.map(item => ((
      <PivotItem
        key={item.id}
        itemKey={item.id}
        onRenderItemLink={() => this.title(item)}
      />
    )));

    return tabItemsFragement;
  };

  title = (item) => {
    const {
      classes,
      i18n,
      fallBackTabItem,
      handleClose,
      availableTabItems,
      onTabItemRender,
      renderExtraContent,
      selectedTabItem,
    } = this.props;
    const classNameArray = [classes.textContainer];

    const localizedName = i18n.getString(item.name);
    if (_.find(availableTabItems, tabItem => tabItem.id === item.id)) {
      const isSelected = selectedTabItem.id === item.id;
      return ((
        <FocusZone disabled>
          <span id={`bingads-campaign-react-sub-page-menu-tabs-tab-${item.id}`}>
            <span
              className={classes.text}
              role={isSelected ? 'heading' : null}
              aria-level={isSelected ? '1' : null}
            >
              {localizedName}
            </span>
            {onTabItemRender && onTabItemRender(item)}
          </span>
          {renderExtraContent(item)}
        </FocusZone>
      ));
    }

    classNameArray.push(classes.notAvailableTextContainer);
    if (fallBackTabItem === item) {
      classNameArray.push(classes.fallBackTextContainer);
    }

    return ((
      <FallbackCallout
        title={localizedName}
        show={fallBackTabItem === item}
        handleClose={handleClose}
        placement="right"
        siteMapId={item.id}
      >
        <div className={classNames(classNameArray)} id={`bingads-campaign-react-sub-page-menu-tabs-tab-${item.id}`}>
          <span className={classes.text}>{localizedName}</span>
          <Glyphicon className={classes.fallbackGlyph} glyph="ba-OpenInNewWindow" />
        </div>
      </FallbackCallout>
    ));
  };

  renderExpandButton = () => {
    const { classes, permissions } = this.props;
    if (!isNavigationRefreshOn(permissions)) {
      return null;
    }

    return (
      <div className={classes.expandButtonContainer}>
        <ExpandButton />
      </div>
    );
  };

  render() {
    const {
      selectedTabItem,
      tabItems,
      responsiveSize,
      i18n,
      classes,
      className,
    } = this.props;

    let orderedTabItems = [];
    let tabItemsFragement = null;

    // tab bar must not be present when there's no selected item. Happens in rare scenarios such as when
    // user switches from an account which has lot of campaings to an account which has none. If the current
    // page is not part of the scope, tab bar will throw an error and crash the page since selectedTabItem is
    // empty.
    if (_.isEmpty(selectedTabItem)) {
      return null;
    }

    const selectedIndex = _.findIndex(tabItems, item => item.id === selectedTabItem.id);

    // rearranging logic.
    // responsive size 0 is show all tabs.
    // responsibe size 1,2,3 corresponds to number of tabs we can show.
    // if responsize size is 0 we show all the tabs.
    // if responsive size is either 1,2,3 and the selectedTab's Index is not within responsive size range, we replace the last item in the tabs with the selected item
    // and fill in the other items.
    // for responsive size = 3 and tabItems = 5 selectedIndex = 3 well have
    // firstSetOfTabs = [0, 1]
    // selectedIndex = [3]
    // restOfTheTabs = [2, 4]
    // after rearranging [0,1,3,2,4]

    if (responsiveSize === 0) {
      // this is when we have space to show all the elements.
      tabItemsFragement = this.generateTabItemsFragment(tabItems);
    } else {
      if (selectedIndex >= responsiveSize) {
        const firstSetOfTabs = tabItems.slice(0, responsiveSize - 1);
        const restOfTheTabs = _.filter(tabItems.slice(responsiveSize - 1, tabItems.length), item => item.id !== selectedTabItem.id);
        orderedTabItems = [...firstSetOfTabs, selectedTabItem, ...restOfTheTabs];
      } else {
        orderedTabItems = tabItems;
      }

      tabItemsFragement = this.generateTabItemsFragment(orderedTabItems);
    }

    let newItems = tabItemsFragement;
    let leftOverItems = [];

    if (responsiveSize > 0) {
      newItems = newItems.slice(0, responsiveSize);
      leftOverItems = orderedTabItems.slice(responsiveSize, orderedTabItems.length);
    }

    if (leftOverItems.length > 0) {
      const moreIcon = {
        iconName: 'More',
      };

      const contextualMenu = (
        <IconButton
          menuProps={this.genereateOverflowMenuItemProps(leftOverItems)}
          ariaLabel={i18n.getString(_TL_('View more'))}
          iconProps={moreIcon}
          componentRef={this.moreButtonRef}
          onRenderMenuIcon={() => null}
        />
      );

      const pivotOverflowItem = (
        <PivotItem
          onRenderItemLink={() => contextualMenu}
          itemKey={DROP_DOWN_PIVOT_ITEM}
          key={DROP_DOWN_PIVOT_ITEM}
        />
      );
      newItems.push(pivotOverflowItem);
    }
    if (tabItems.length === 1) {
      return (
        <div className={classNames('bingads-campaign-react-tab-bar', classes.menuContainer, className)}>
          {this.renderExpandButton()}
          <span
            role="heading"
            aria-level="1"
            className={classNames('subPageMenu-singleItem', classes.singleItem)}
          >
            {i18n.getString(tabItems[0].name)}
          </span>
        </div>
      );
    }

    return (
      <>
        {this.renderExpandButton()}
        <Pivot
          selectedKey={selectedTabItem ? selectedTabItem.id : null}
          defaultSelectedIndex="0"
          aria-label="Sub Page Menu"
          linkSize={PivotLinkSize.large}
          className={this.menuContainerPartClassName}
          onLinkClick={this.onSelectPivotItem}
        >
          {newItems}
        </Pivot>
      </>
    );
  }
}

TabBar.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getTabBarStyles({}, {}),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  className: PropTypes.string.isRequired,
  responsiveSize: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  tabItems: siteMapPropType.isRequired,
  availableTabItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    route: PropTypes.string,
    selected: PropTypes.bool,
    campaignTypes: PropTypes.arrayOf(PropTypes.oneOf([
      CampaignType.None,
      CampaignType.Default,
      CampaignType.Shopping,
      CampaignType.DynamicSearchAds,
      CampaignType.Audience,
      CampaignType.Smart,
      CampaignType.Hotel,
      CampaignType.All,
    ])),
    available: PropTypes.bool,
  })).isRequired,
  selectedTabItem: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    route: PropTypes.string,
    selected: PropTypes.bool,
    campaignTypes: PropTypes.arrayOf(PropTypes.oneOf([
      CampaignType.None,
      CampaignType.Default,
      CampaignType.Shopping,
      CampaignType.DynamicSearchAds,
      CampaignType.Audience,
      CampaignType.Smart,
      CampaignType.Hotel,
      CampaignType.All,
    ])),
    available: PropTypes.bool,
  }).isRequired,
  fallBackTabItem: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    route: PropTypes.string,
    selected: PropTypes.bool,
    campaignTypes: PropTypes.arrayOf(PropTypes.oneOf([
      CampaignType.None,
      CampaignType.Default,
      CampaignType.Shopping,
      CampaignType.DynamicSearchAds,
      CampaignType.Audience,
      CampaignType.Smart,
      CampaignType.Hotel,
      CampaignType.All,
    ])),
    available: PropTypes.bool,
  }),
  onTabItemRender: PropTypes.func,
  renderExtraContent: PropTypes.func,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      NavigationRefresh: PropTypes.bool,
    }),
  }).isRequired,
};

TabBar.defaultProps = {
  responsiveSize: 0,
  onTabItemRender: null,
  renderExtraContent: () => null,
};
export const StyledTabBar =
  withPermissions(withScope(withDefaultStyles(TabBar, getTabBarStyles)));
